// Handling for an unrecoverable 401 (prod bug, 2026-09-18: a browser tab held a
// Cognito session that had expired WEEKS earlier — every screen's dropdowns/lists
// silently emptied, console filled with 401s, and the user was never sent back to
// sign-in). No refresh-token mechanism exists to try first: AuthContext.tsx's
// `beginCognitoLogin` requests Cognito's hosted UI with `response_type=token`
// (the implicit grant), which never issues a refresh token — so the only
// recoverable action on a dead/expired token is sending the user back through the
// same sign-in flow the login screen itself uses (see `_authenticated.tsx`'s
// `beforeLoad`, which does exactly this for a navigation-time unauthenticated
// visit; this module is the equivalent for a LIVE 401 arriving mid-session).
//
// Deliberately framework-light (no React/router import) so the redirect-once /
// loop-avoidance logic is testable as plain logic — mirrors src/api/client.ts's
// own no-React-dependency discipline. Wired up once by main.tsx's `AppRouter`
// (which has both the real router and `useAuth()`'s `logout` in scope) via
// src/api/client.ts's `configureUnauthorizedHandler` seam.

export interface SessionExpiryDeps {
  /** Current route pathname (e.g. `router.state.location.pathname`). */
  pathname: string;
  /** Current full location (pathname+search+hash, e.g.
   * `router.state.location.href`) to preserve as the post-login return target. */
  currentHref: string;
  /** Clears the persisted + in-memory session — AuthContext's `logout`. */
  clearSession: () => void;
  /** Navigates to the sign-in flow with the return target. */
  redirectToLogin: (returnTo: string) => void;
}

/** Never redirect FROM these routes: /login's own GET /me (AuthContext's
 * `refreshMemberships`) already has its own carefully race-proofed 401→logout
 * handling (see that function's comment re: the 2026-08-25 cold-start race) —
 * running this handler too would double-handle that 401. /auth-callback briefly
 * has no token at all while it parses the redirect hash. Routing either back
 * through here risks a redirect loop. */
const EXEMPT_PATH_PREFIXES = ['/login', '/auth-callback'];

function isExemptPath(pathname: string): boolean {
  return EXEMPT_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

// Module-level latch: many parallel queries can each 401 off the same dead token
// within milliseconds of each other (exactly today's bug report — every dropdown
// on screen empty at once). First 401 wins the redirect; the rest are no-ops
// rather than a pile of duplicate logout/navigate calls. Reset on a fresh
// sign-in (AuthProvider does this whenever `token` transitions to a real value)
// so a LATER session going stale can still trigger the redirect again.
let redirecting = false;

/** Handles one unrecoverable 401: clears the session and redirects to sign-in,
 * exactly once, unless already on an exempt route. */
export function handleUnauthorizedResponse(deps: SessionExpiryDeps): void {
  if (redirecting) return;
  if (isExemptPath(deps.pathname)) return;
  redirecting = true;
  deps.clearSession();
  deps.redirectToLogin(deps.currentHref);
}

/** Re-arms the latch. Called by AuthProvider on every successful sign-in, and by
 * this module's own tests between cases (the latch is module state, so it
 * otherwise leaks across both). */
export function resetSessionExpiryLatch(): void {
  redirecting = false;
}
